<template>
  <TypeformQuestion
    center
    :question="title"
    :subtitle="totalCopy"
    :loading="validating || store.applicationForm.submitting"
    @prevStep="$emit('prevStep')"
    @nextStep="$emit('nextStep')">

    <div
      v-if="initialSyncComplete"
      class="space-y-6 text-left bg-white p-4 lg:p-6 rounded-md border border-slate-200">

      <ScreeningQuestionAnswer
        v-for="(question, index) in questions"
        :key="question.id"
        :index="index"
        :question="question"
        :number="offset + index + 1"
        :total="totalQuestions"
        class="mb-8"
        v-model:answer="store.screeningQuestionAnswers[offset + index]"
        @update:answer="debouncePushToLocalStorage"
      />

    </div>

  </TypeformQuestion>
</template>

<script setup>
import ScreeningQuestionAnswer from "./ScreeningQuestionAnswer.vue";
import TypeformQuestion from "./TypeformQuestion.vue";
import {useCandidateApplicationFormStore} from "../stores/CandidateApplicationFormStore.js";
import {computed, onBeforeMount, onMounted, toRaw} from "vue";
import {debounce} from "../marketing-site/lightweight-debounce";
import {useQuickApplyScreeningQuestions} from "../composables/QuickApplyScreeningQuestions";

const emits = defineEmits(["nextStep", "prevStep", "update:validating", "update:validator"]);
const props = defineProps({
  questions: Array,
  offset: Number,
  totalCopy: String,
  validating: Boolean,
  isLastStep: Boolean,
  totalQuestions: Number,
});

const store = useCandidateApplicationFormStore();

const title = computed(() => {
  if (props.isLastStep) return "Last step! Make your application stand out 💪";
  return "Make your application stand out 💪";
});

const {
  initialSyncComplete,
  handlePushToLocalStorage,
  handleGetFromLocalStorage,
} = useQuickApplyScreeningQuestions();

const pushToLocalStorage = async () => {
  await handlePushToLocalStorage(
    toRaw(store.screeningQuestionAnswers),
    store.applicationForm.uuid
  );
};

const getFromLocalStorage = async () => {
  await handleGetFromLocalStorage(
    store.applicationForm.uuid,
    store.applicationForm.screeningQuestions,
    (index) => store.screeningQuestionAnswers[index]?.answer,
    (index, answer) => store.screeningQuestionAnswers[index] = answer,
  );
};

const debouncePushToLocalStorage = debounce(pushToLocalStorage, 750);

onBeforeMount(() => getFromLocalStorage());

onMounted(() => {
  store.setFurthestStep(5 + props.offset, "Screening Questions Page #" + (props.offset + 1));

  emits("update:validating", false);
  emits("update:validator", async () => {

    for (let i = 0; i < props.questions.length; i++) {
      const question = store.screeningQuestionAnswers[i + props.offset];

      if (!question?.answer) {
        toast.warning(`Please answer question #${i + 1}`);
        return false;
      }
    }

    return true;
  });
});
</script>
